/**
 * Behavior rating rules for the Nov 2026 cycle (Zohal + Anna, Oct 29–30):
 * - each of the five FINN values is rated 1–5;
 * - the suggested overall Behavior rating is their average, rounded normally
 *   (3.2 / 3.4 → 3, 3.6 / 3.8 → 4; five whole numbers never average to x.5);
 * - a rating BELOW the suggestion needs a note. It stays in the tool (not shared with the
 *   employee, not copied into Culture Amp) and goes to the AI output as context.
 */
import { FINN_VALUES } from '../../data/managerReviewData';
import type { BehaviorData } from '../../types/managerReviewV2';

export function behaviorAverage(behaviorsData: Record<string, BehaviorData>): number | null {
  const ratings = FINN_VALUES.map(v => behaviorsData[v.name]?.rating).filter((r): r is number => typeof r === 'number');
  if (ratings.length !== FINN_VALUES.length) return null;
  return ratings.reduce((a, b) => a + b, 0) / ratings.length;
}

export function suggestedBehaviorRating(behaviorsData: Record<string, BehaviorData>): number | null {
  const avg = behaviorAverage(behaviorsData);
  return avg === null ? null : Math.round(avg);
}

/** Rated below the suggestion → the note is required. */
export function isRoundedDown(behaviorsRating: number | null, suggested: number | null): boolean {
  return behaviorsRating !== null && suggested !== null && behaviorsRating < suggested;
}

/** Rated below the suggestion without a note: the Behaviors tab is not done. */
export function missingRoundDownNote(
  behaviorsData: Record<string, BehaviorData>,
  behaviorsRating: number | null,
  note: string,
): boolean {
  return isRoundedDown(behaviorsRating, suggestedBehaviorRating(behaviorsData)) && !note.trim();
}
